import { GoalsConfig } from "../views/ChallengeAdmin/ChallengeAdmin";

/* eslint-disable */
import { WorkoutType } from '../plugins/ActivityTrackerPlugin'

export interface ActivityProgress {
    actualMaxHeartRate: number;
    calculatedMaxHeartRate: number;
    caloriesGoal: number;
    caloriesPercentage: number;
    caloriesProjectedPercentage: number;
    caloriesTotal: number;
    country: string;
    divisionName: string;
    email: string;
    endDate: number;
    firstName: string;
    goalId: GoalId;
    intensiveMinutesGoal: number;
    intensiveMinutesPercentage: number;
    intensiveMinutesProjectedPercentage: number;
    intensiveMinutesTotal: number;
    lastName: string;
    lastSync: number;
    ofPeriodPassedPercentage: number;
    period: PeriodType;
    persistenceGoal: number | null;
    persistencePercentage: number;
    persistenceTotal: number;
    startDate: number;
    stepsGoal: number;
    stepsPercentage: number;
    stepsProjectedPercentage: number;
    stepsTotal: number;
    totalPercentage: number;
    totalProjectedPercentage: number;
    userId: UserId;
}

export enum ActivityType {
    CALORIES = 'CALORIES',
    CARDIO_MINUTES = 'CARDIO_MINUTES',
    CUSTOM = 'CUSTOM',
    FAT_BURN_MINUTES = 'FAT_BURN_MINUTES',
    HEART_RATE = 'HEART_RATE',
    HEIGHT = 'HEIGHT',
    MAX_HEART_RATE = 'MAX_HEART_RATE',
    PEAK_MINUTES = 'PEAK_MINUTES',
    PEAK_SECONDS = 'PEAK_SECONDS',
    STEPS = 'STEPS',
    WEIGHT = 'WEIGHT',
    DISTANCE = 'DISTANCE',
    EXERCISE_MIN = 'EXERCISE_MIN',
    SLEEP_MIN = 'SLEEP_MIN',
}

export interface UploadActivityDto {
    activityType: ActivityType;
    diaryEntries: AppleHealthDiaryEntry[];
    endDate: number;
    startDate: number;
    timeZone: string;
}

export interface ActivityEntry {
    startDate: number;
    endDate: number;
    activityType: string;
    calories: number;
    duration: number;
    steps: number;
    distance: number;
    source: string;
    originalId: string;
    /** Average heart rate in bpm (optional, client-computed) */
    avgHeartRate?: number;
    /** Average power in watts (optional, client-computed) */
    avgPower?: number;
    /** Average cadence in rpm/spm (optional; reserved for Health Connect parity) */
    avgCadence?: number;
    /** Average speed in m/s (optional; reserved for Health Connect parity) */
    avgSpeed?: number;
    /** Elevation gain in meters (optional, client-computed) */
    elevationGain?: number;
    /** Elevation loss in meters (optional, client-computed) */
    elevationLoss?: number;
    /** Maximum speed in m/s (optional, client-computed) */
    maxSpeed?: number;
    /** GPS route points recorded during the activity (optional) */
    route?: Route[];
}

// GPS route point interface for location data
export interface GPSRoutePoint {
    lat: number; // Latitude
    lng: number; // Longitude
    ts: number; // Unix timestamp in milliseconds
    alt?: number; // Altitude in meters
    spd?: number; // Speed in m/s
}

// Route interface for activity route data
export interface Route {
    /** Latitude coordinate in decimal degrees */
    lat: number;
    /** Longitude coordinate in decimal degrees */
    lng: number;
    /** Unix timestamp in milliseconds when this point was recorded */
    ts: number;
    /** Altitude in meters (optional) */
    alt?: number;
    /** Speed in meters per second (optional) */
    spd?: number;
    /** Heart rate in beats per minute (optional) */
    hr?: number;
    /** Power in watts (optional) */
    pwr?: number;
    /** Air temperature in degrees Celsius (optional) */
    tmp?: number;
}

export interface AppleHealthDiaryEntry {
    endDate: number;
    startDate: number;
    //timeOffset: number | null;
    value: number;
}

export interface GetCompanyStatisticsByPeriod$Response {
  stats: Stats[]
}

export interface GetCompanyStatisticsByPeriod$Request {
    companyId: string;
    period: string;
    startDate: string;
    endDate: string;
}

export interface ChangePassword$Request {
    newPassword: SensitiveString;
    oldPassword: SensitiveString;
}

export interface ChangePassword$Response {
}

export interface SaveImage$Request {
    file: any
    companyId: CompanyId
}

export interface SaveImage$Response {
  fileDto: FileDto;
}

export interface ChangeUserLanguage$Request {
    lang: Lang;
}

export interface ChangeUserLanguage$Response {
}

export interface ChangeUserScreenOrientation$Request {
    screenOrientation: ScreenOrientation;
}

export interface ChangeUserScreenOrientation$Response {
}

export interface CompanyCountriesDto {
    companyId: CompanyId;
    countries: string[];
}

export interface CompanyGoalResultsDto {
    activityProgress: ActivityProgress[];
    goalId: GoalId;
}

export type CompanyId = string;

export type FileId = string;

export interface CompanyRankDto {
    companyId: CompanyId;
    companyName: string;
    participants: number;
    percentage: number;
    rank: number;
}

export interface FileDto {
  id: FileId;
  thumbnail: string;
  full: string;
}

export interface CompanyStatistics {
    companyEnergyStatistics: CompanyStatistics$CompanyEnergyStatistics;
    companyIntensiveMinutesStatistics: CompanyStatistics$CompanyIntensiveMinutesStatistics;
    companyStepsStatistics: CompanyStatistics$CompanyStepsStatistics;
}

export interface CompanyStatistics$CompanyEnergyStatistics {
    bigMacs: number;
    totalKcal: number;
}

export interface CompanyStatistics$CompanyIntensiveMinutesStatistics {
    co2EmissionPrevented: number;
    totalMin: number;
}

export interface CompanyStatistics$CompanyStepsStatistics {
    roundsAroundTheWorld: number;
    totalDistance: number;
    totalSteps: number;
}

export interface CountryActivityProgress {
    caloriesTotal: number;
    country: string;
    intensiveMinutesTotal: number;
    participants: number;
    persistenceTotal: number;
    stepsTotal: number;
    totalPercentage: number;
}

export interface CreateGoal$Request {
    activityFormulaTarget: number | null;
    chatEnabled: boolean;
    collectiveGoal: number | null;
    companyId: CompanyId;
    customGoal: number | null;
    customName: string | null;
    customType: CustomType | null;
    customUnit: string | null;
    dailyPersistenceGoal: number | null;
    dailyStepsGoal: number | null;
    description: string;
    divisionId: DivisionId;
    endDate: number;
    hideCountriesTab: boolean;
    hideGroupsTab: boolean;
    hidePeopleTab: boolean;
    manualEntryEnabled: boolean;
    persistenceGoal: number | null;
    progressType: ProgressType;
    startDate: number;
    title: string;
    voucher: string;
    pointsLogic: any[] | null;
    totalPointsLogic: any[] | null;
    roundDailyPoints: boolean;
    autoJoinEnabled: boolean;
    simpleManualEntry: boolean;
    maxManualEntryDays: number;
    includeInTotalPointsChallenges: Record<GoalId, boolean>
    dailyMax: number | null;
    leaderboards: string[];
    goals: GoalsConfig | null;
    avgAgg: boolean;
}

export interface CreateGoal$Response {
}

export interface CreateUser$Request {
    companyId: CompanyId;
    emails: string[];
    lang: Lang;
}

export interface CreateUser$Response {
    ids: UserId[];
}

export interface CreateUserOwnedCompany$Request {
    companyName: string;
}

export interface CreateUserOwnedCompany$Response {
    companyId: CompanyId;
    signUpCode: string;
}

export interface CustomEntry$Request {
    amount: number;
    customType: CustomType | null;
    date: string;
    goalId: GoalId;
    pointsLogicId: string | null;
    userId: UserId | null;
}

export interface CustomEntry$Response {
}

export enum CustomType {
    ACTIVITY_INDEX = 'ACTIVITY_INDEX',
    CUSTOM = 'CUSTOM',
    CYCLING_HR = 'CYCLING_HR',
    CYCLING_KM = 'CYCLING_KM',
    CYCLING_MI = 'CYCLING_MI',
    CYCLING_MIN = 'CYCLING_MIN',
    DISTANCE_KM = 'DISTANCE_KM',
    DISTANCE_MI = 'DISTANCE_MI',
    EXERCISE_HR = 'EXERCISE_HR',
    EXERCISE_MIN = 'EXERCISE_MIN',
    MINDFULNESS_HR = 'MINDFULNESS_HR',
    MINDFULNESS_MIN = 'MINDFULNESS_MIN',
    OUTDOOR_TIME_HR = 'OUTDOOR_TIME_HR',
    OUTDOOR_TIME_MIN = 'OUTDOOR_TIME_MIN',
    PLANKING_HR = 'PLANKING_HR',
    PLANKING_MIN = 'PLANKING_MIN',
    RUNNING_HR = 'RUNNING_HR',
    RUNNING_KM = 'RUNNING_KM',
    RUNNING_MI = 'RUNNING_MI',
    RUNNING_MIN = 'RUNNING_MIN',
    SLEEPING_HR = 'SLEEPING_HR',
    SLEEP_HR = 'SLEEP_HR',
    SLEEP_MIN = 'SLEEP_MIN',
    SWIMMING_HR = 'SWIMMING_HR',
    SWIMMING_KM = 'SWIMMING_KM',
    SWIMMING_M = 'SWIMMING_M',
    SWIMMING_MI = 'SWIMMING_MI',
    SWIMMING_MIN = 'SWIMMING_MIN',
    SWIMMING_YD = 'SWIMMING_YD',
    WALKING_HR = 'WALKING_HR',
    WALKING_KM = 'WALKING_KM',
    WALKING_MEETING_MEETINGS = 'WALKING_MEETING_MEETINGS',
    WALKING_MEETING_MIN = 'WALKING_MEETING_MIN',
    WALKING_MI = 'WALKING_MI',
    WALKING_MIN = 'WALKING_MIN',
    WATER_DRINKING_CUP = 'WATER_DRINKING_CUP',
    WATER_DRINKING_GLASS = 'WATER_DRINKING_GLASS',
    WATER_DRINKING_ML = 'WATER_DRINKING_ML',
    WATER_DRINKING_OZ = 'WATER_DRINKING_OZ',
    YOGA_HR = 'YOGA_HR',
    YOGA_MIN = 'YOGA_MIN',
    STRENGTH_TRAINING_HR = 'STRENGTH_TRAINING_HR',
    STRENGTH_TRAINING_MIN = 'STRENGTH_TRAINING_MIN',
    OTHER_MIN = 'OTHER_MIN',

    STEPS = 'STEPS',
    CALORIES = 'CALORIES',
    INTENSIVE_MINUTES = 'INTENSIVE_MINUTES',

    POINTS = 'POINTS',
    TOTAL_POINTS = 'TOTAL_POINTS',

    CUSTOM_SIMPLE = 'CUSTOM_SIMPLE',
    CUSTOM_WITH_AMOUNT = 'CUSTOM_WITH_AMOUNT',

    // New manual entry challenges.
    BOOK_READING_MIN = 'BOOK_READING_MIN',
    BOOK_READING_BOOKS = 'BOOK_READING_BOOKS',
    BOOK_READING_PAGES = 'BOOK_READING_PAGES',

    ENCOURAGING_PEOPLE = 'ENCOURAGING_PEOPLE',
    CAR_FREE_DAYS = 'CAR_FREE_DAYS',
    ALCOHOL_ABSTINENCE = 'ALCOHOL_ABSTINENCE',
    SQUATTING = 'SQUATTING',
    CONTRAST_SHOWER_SEC = 'CONTRAST_SHOWER_SEC',
    FINANCE_TRACKING = 'FINANCE_TRACKING',
    HEALTHY_COOKING_MEALS = 'HEALTHY_COOKING_MEALS',
    DEEP_BREATHING_MIN = 'DEEP_BREATHING_MIN',
    DAILY_JOURNALING = 'DAILY_JOURNALING',
    PUSH_UPS = 'PUSH_UPS',
    PULL_UPS = 'PULL_UPS',
    CREATIVE_ARTS_MIN = 'CREATIVE_ARTS_MIN',
    SOCIALIZING_WITH_FRIENDS_MIN = 'SOCIALIZING_WITH_FRIENDS_MIN',
    COLD_PLUNGE_SEC = 'COLD_PLUNGE_SEC',
    INTERMITTENT_FASTING_HR = 'INTERMITTENT_FASTING_HR',
    GRATITUDE_JOURNALING = 'GRATITUDE_JOURNALING',
    LANGUAGE_LEARNING_MIN = 'LANGUAGE_LEARNING_MIN',
    HOBBY_DEVELOPMENT_MIN = 'HOBBY_DEVELOPMENT_MIN',
    NATURAL_LIGHT_EXPOSURE = 'NATURAL_LIGHT_EXPOSURE',
    SMOKING_CESSATION = 'SMOKING_CESSATION',
    PHONE_FREE_TIME_HR = 'PHONE_FREE_TIME_HR',
    //CAFFEINE_REDUCTION = 'CAFFEINE_REDUCTION',
    SCREEN_FREE_MEALS = 'SCREEN_FREE_MEALS',
    DAILY_FLOSSING = 'DAILY_FLOSSING',
    //STRESS_LEVEL_MONITORING = 'STRESS_LEVEL_MONITORING',
    //MOOD_TRACKING = 'MOOD_TRACKING',
}

export interface DeleteGoal$Request {
    goalId: GoalId;
}

export interface DeleteGoal$Response {
}

export interface DeleteUser$Request {
    companyId: CompanyId;
    userId: UserId;
}

export interface DeleteUser$Response {
}

export interface DeleteUserFromGarmin$Response {
}

export interface DeleteUserOwnedCompany$Request {
    companyId: CompanyId;
}

export interface DeleteUserOwnedCompany$Response {
    companyName: string;
}

export interface DivisionActivityProgress {
    caloriesTotal: number;
    country: string;
    divisionId: DivisionId;
    divisionName: string;
    intensiveMinutesTotal: number;
    participants: number;
    persistenceTotal: number;
    stepsTotal: number;
    totalPercentage: number;
}

export interface DivisionDto {
    companyId: CompanyId;
    id: DivisionId;
    name: string;
    locked: boolean;
    creatorUserId: UserId;
    members: number;
    categoryId: DivisionCategoryId;
}

export interface DivisionRow {
    id: DivisionId;
    companyId: CompanyId;
    name: string;
    members: number;
    maxSize: number;
    locked: boolean;
    archived: boolean;
    createdAt: number;
    creatorUserId: UserId;
    categoryId: DivisionCategoryId;
}

export interface NotificationRow {
    id: NotificationId;
    creatorUserId: UserId;
    companyId: CompanyId;
    type: NotificationType;
    title: string;
    message: string;
    ctaText: string | null;
    ctaUrl: string | null;
    imageFileId: FileId | null;
    link: string;
    lang: Lang;
    goalId: GoalId | null;
    divisionId: DivisionId | null;
    country: string | null;
    scheduled: boolean;
    scheduledTime: number;
    sendPushNotification: boolean;
    sendEmail: boolean;
    sent: boolean;
    createdAt: number;
    updatedAt: number;
}

export interface NotificationDto {
    id: NotificationId;
    creatorUserId: UserId;
    companyId: CompanyId;
    type: NotificationType;
    title: string;
    message: string;
    ctaText: string | null;
    ctaUrl: string | null;
    imageFileId: FileId | null;
    imageThumbUrl: string | null;
    imageFullUrl: string | null;
    link: string;
    lang: Lang;
    goalId: GoalId | null;
    divisionId: DivisionId | null;
    country: string | null;
    scheduled: boolean;
    scheduledTime: number;
    sendPushNotification: boolean;
    sendEmail: boolean;
    sent: boolean;
    createdAt: number;
    updatedAt: number;

    readBy: number;
    sentTo: number;
}

export interface UserPreferenceDto {
  id: UserPreferenceId;
  type: UserPreferenceType;
  subscribed: boolean;
}

export interface UserNotificationDto {
  // From user_notification table.
  id: UserNotificationId;
  read: boolean;
  sentAt: number;
  // From notification table.
  notificationId: NotificationId;
  creatorUserId: UserId;
  companyId: CompanyId;
  type: NotificationType;
  title: string;
  message: string;
  ctaText: string;
  ctaUrl: string;
  // From file table.
  imageFileId: FileId | null;
  imageThumbUrl: string | null;
  imageFullUrl: string | null;
  // Other
  senderName: string;
  companyName: string;
}

export type DivisionId = string | null;

export type NotificationId = string | null;

export type UserNotificationId = string | null;

export type UserPreferenceId = string | null;

export interface EndUserThirdPartyIntegration$Request {
}

export interface EndUserThirdPartyIntegration$Response {
}

export interface ExportGoalResults$Request {
    goalId: GoalId;
    supportsBackground?: boolean;
}

export interface ExportChatMessages$Request {
    goalId: GoalId;
}

export interface ExportChatImages$Request {
    goalId: GoalId;
    supportsBackground?: boolean;
}

export interface ExportGoalResults$Response {
    fileContent: number[] | null;
    fileMediaType: string | null;
    fileName: string | null;
    backgroundQueued?: boolean;
}

export interface ExportChatMessages$Response {
    fileContent: number[];
    fileMediaType: string;
    fileName: string;
}

export interface ExportChatImages$Response {
    fileUrl: string | null;
    fileSize: number | null;
    backgroundQueued?: boolean;
}


export interface FinalizeGarminIntegration$Request {
    oauthToken: string;
    oauthVerifierValue: string;
    redirectUrl: string;
}

export interface FinalizeGarminIntegration$Response {
    redirectView: LeanRedirectView;
}

export enum Gender {
    FEMALE = 'FEMALE',
    MALE = 'MALE',
}

export interface GetAppleHealthSyncStart$Request {
}

export interface GetAppleHealthSyncStart$Response {
    lastSyncDatePerActivity: LastSyncDatePerActivityDto;
}

export interface GetAppleHealthSyncTimes$Request {
    forceLongSync: boolean;
}

export interface GetGoogleFitAndroidSyncTimes$Request {
    forceLongSync: boolean;
}

export interface GetChatUserToken$Request {
}

export interface GetChatUserToken$Response {
    userToken: string;
}

export interface GetCompanyDivisions$Request {
}

export interface GetCompanyDivisions$Response {
    divisionDtos: DivisionDto[];
}

export interface GetCompanyRankingFiltered$Request {
    companyId: CompanyId;
    month: string;
}

export interface GetCompanyRankingFiltered$Response {
    companyIsExcludedFromRanking: boolean;
    companyRanking: CompanyRankDto[];
    periodPercentage: number;
}

export interface GetCompanySignUpCode$Request {
    companyId: CompanyId;
}

export interface GetCompanySignUpCode$Response {
    signUpCode: string;
}

export interface GetCompanyEmployees$Request {
    companyId: CompanyId;
}

export interface GetCompanyEmployees$Response {
    employees: string;
}


export interface SetCompanyEmployees$Request {
    companyId: CompanyId;
    employees: number;
    month: string;
}

export interface SetCompanyEmployees$Response {
}

export interface GetGoalCountryProgress$Request {
    goalId: GoalId;
    sum: boolean;
}

export interface GetGoalCountryProgress$Response {
    countryActivityProgress: CountryActivityProgress[];
    periodPercentage: number;
}

export interface GetGoalGroupProgress$Request {
    goalId: GoalId;
    groupView: GroupView;
    sum: boolean;
}

export interface GetGoalGroupProgress$Response {
    divisionActivityProgress: DivisionActivityProgress[];
    periodPercentage: number;
}

export interface GetGoalIndividualProgress$Request {
    countries: string[];
    divisionIds: DivisionId[];
    goalId: GoalId;
    skipJoin: boolean;
    token: string;
}

export interface GetChallengesWithProgress$Request {
    version: string;
}

export interface GetGoalIndividualProgress$Response {
    activityProgress: { [key: string ]: ActivityProgress;};
}

export interface GetGoalsWithProgress$Request {
}

export interface GetGoalsWithProgress$Response {
    goals: GoalWithProgress[];
}

export interface GetSingleChallengeWithProgress$Request {
    goalId: GoalId;
    version: string;
    groupSum: boolean;
    token?: string;
}

export interface GetSingleChallengeWithProgress$Response {
    goal: GoalWithProgress;
    companyCountries: CompanyCountriesDto[];
    divisionDtos: DivisionDto[];
    divisionCategories: DivisionCategoryRow[];
}

export interface GetLeaderboardCountries$Request {
    goalId: GoalId;
    sum: boolean;
    token: string;
}

export interface GetLeaderboardCountries$Response {
    companyCountries: CompanyCountriesDto[];
    countryActivityProgress: CountryActivityProgress[];
    divisionDtos: DivisionDto[];
    goals: GoalWithProgress[];
    periodPercentage: number;
}

export interface GetLeaderboardGroups$Request {
    goalId: GoalId;
    groupView: GroupView;
    sum: boolean;
    token: string;
}

export interface GetLeaderboardGroups$Response {
    companyCountries: CompanyCountriesDto[];
    divisionActivityProgress: DivisionActivityProgress[];
    divisionDtos: DivisionDto[];
    goals: GoalWithProgress[];
    periodPercentage: number;
}

export interface GetLeaderboardPeople$Request {
    countries: string[];
    divisionIds: DivisionId[];
    goalId: GoalId;
    skipJoin: boolean;
    token: string;
}

export interface GetLeaderboardPeople$Response {
    activityProgress: { [key: string ]: ActivityProgress;};
    companyCountries: CompanyCountriesDto[];
    divisionDtos: DivisionDto[];
    goals: GoalWithProgress[];
}

export interface LeaderboardProgress {
    userId: UserId;
    rank: number;
    name: string;
    teamId: DivisionId;
    teamName: string;
    country: string;
    result: number;
    percentage: number;
    data: string | null;
    participants?: number;
    isSeparatorForAbove?: boolean;
    isSeparatorForBelow?: boolean;
    isBeforeSelf?: boolean;
    isSelf?: boolean;
}

export interface GetGroupLeaderboardProgress$Request {
    goalId: GoalId;
    categoryId: string;
    groupView: GroupView;
    sum: boolean;
    token: string;
    start: number;
    size: number;
    search?: string;
}

export interface GetGroupLeaderboardProgress$Response {
    data: Array<LeaderboardProgress>;
    totalRowCount: number;
    ofPeriodPassedPercentage: number;
}

export interface GetSpecificCompanyStatistics$Request {
    companyId: CompanyId;
}

export interface GetSpecificCompanyStatistics$Response {
    companyStatistics: CompanyStatistics;
    showTable: boolean;
}

export interface GetSpecificCompanyUsers$Request {
    companyId: CompanyId;
    divisionIds?: string; // JSON string containing categoryId and divisionId values
}

export interface GetSpecificCompanyUsers$Response {
    companyUsers: UserDto[];
}

export interface GetCompanyTeams$Request {
    companyId: CompanyId;
}

export interface GetCompanyTeams$Response {
    companyTeams: DivisionRow[];
}

export interface GetDivisions$Request {
    companyId: CompanyId;
    //categoryId?: DivisionCategoryId;
}

export interface GetDivisions$Response {
    companyGroups: DivisionRow[];
}

// Company Group types
export type DivisionCategoryId = string;

export enum DivisionCategoryType {
    AREA = 'AREA',
    CLUB = 'CLUB',
    DEPARTMENT = 'DEPARTMENT',
    DIVISION = 'DIVISION',
    LOCATION = 'LOCATION',
    REGION = 'REGION',
    STATE = 'STATE',
    CUSTOM = 'CUSTOM'
}

export interface DivisionCategoryRow {
    id: DivisionCategoryId;
    companyId: CompanyId;
    type: DivisionCategoryType;
    customName: string | null;
    required?: boolean;
    minSize?: number;
    maxSize?: number;
    selectionLocked?: boolean;
    switchingDisabled?: boolean;
    usersCanCreate?: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface GetDivisionCategories$Request {
    // No parameters needed - returns all categories for all companies
}

export interface GetDivisionCategories$Response {
    groups: DivisionCategoryRow[];
}

export interface CreateDivisionCategory$Request {
    companyId: CompanyId;
    type: DivisionCategoryType;
    customName?: string; // Required only for CUSTOM type
}

export interface CreateDivisionCategory$Response {
    groupId: DivisionCategoryId;
}

export interface DeleteDivisionCategory$Request {
    groupId: DivisionCategoryId;
    companyId: CompanyId;
}

export interface DeleteDivisionCategory$Response {
    success: boolean;
}

export interface CreateTeam$Request {
    companyId: CompanyId;
    name: string;
    isUserCreated: boolean
    lock: boolean
    groupId?: DivisionCategoryId; // Optional group assignment
}

export interface CreateTeam$Response {
    divisionId: DivisionId;
}

export interface CreateDivision$Request {
    companyId: CompanyId;
    name: string;
    isUserCreated: boolean
    lock: boolean
    categoryId: DivisionCategoryId; // Required category assignment
}

export interface CreateDivision$Response {
    divisionId: DivisionId;
}

export interface CreateAnnouncement$Request {
    companyId: CompanyId;
    title: string;
    message: string;
    ctaText: string | null;
    ctaUrl: string | null;
    imageFileId: FileId | null;
    scheduledTime: string | null;
    goalId: GoalId | null;
    divisionId: DivisionId | null;
    country: string | null;
    lang: string | null;
    sendPushNotification: boolean;
    sendEmail: boolean;
    scheduledTimeZone: string | null;
}

export interface CreateAnnouncement$Response {
  notificationId: NotificationId;
}

export interface EditAnnouncement$Request {
    id: NotificationId;
    title: string;
    message: string;
    ctaText: string | null;
    ctaUrl: string | null;
    imageFileId: FileId | null;
    scheduledTime: string | null;
    goalId: GoalId | null;
    divisionId: DivisionId | null;
    country: string | null;
    lang: string | null;
    sendPushNotification: boolean;
    sendEmail: boolean;
    scheduledTimeZone: string | null;
}

export interface EditAnnouncement$Response {
  notificationId: NotificationId;
}

export interface GetAnnouncementFilters$Request {
  companyId: CompanyId;
  goalId: GoalId | null;
  divisionId: DivisionId | null;
  country: string | null;
  lang: Lang | null;
  scheduledTime: string | null;
  scheduledTimeZone: string | null;
  editMode: boolean;
  notificationId: NotificationId | null;
}

export interface GetAnnouncementFilters$Response {
  announcementLimitExceeded: boolean;
  totalUserCount: number;
  pushEnabledUserCount: number;
  emailEnabledUserCount: number;
  challenges: object[];
  teams: object[];
  countries: object[];
  languages: object[];
}

export interface GetAnnouncements$Request {
  companyId: CompanyId;
}

export interface GetAnnouncements$Response {
  announcements: NotificationDto[];
}

export interface GetUserNotifications$Request {
}

export interface GetUserNotifications$Response {
  userNotifications: UserNotificationDto[];
}

export interface MarkUserNotificationRead$Request {
  userNotificationId: NotificationId;
}

export interface MarkUserNotificationRead$Response {

}

export interface GetUserPreferences$Request {
}

export interface GetUserPreferences$Response {
  preferences: UserNotificationDto[];
}

export interface EditUserPreferences$Request {
  isDuringRegistration?: boolean;
  preferences: UserPreferenceDto[];
}

export interface EditUserPreferences$Response {

}

export interface RenameTeam$Request {
    divisionId: DivisionId;
    name: string;
}

export interface RenameTeam$Response {
}

export interface DeleteTeams$Request {
    divisionIds: DivisionId[];
}

export interface DeleteTeams$Response {
}

export interface DeleteAnnouncements$Request {
    notificationIds: NotificationId[];
}

export interface DeleteAnnouncements$Response {
}

export interface DeleteFile$Request {
    fileId: FileId;
}

export interface DeleteFile$Response {
}

export interface LockTeams$Request {
    divisionIds: DivisionId[];
}

export interface LockTeams$Response {
}

export interface UnlockTeams$Request {
    divisionIds: DivisionId[];
}

export interface UnlockTeams$Response {
}

export interface AssignTeamsRandomly$Request {
    companyId: CompanyId;
    includeUsersWithTeams: boolean;
    goalId?: GoalId;
}

export interface FilterItemDto {
  id: string;
  title: string;
  count: number;
}

export interface GetAssignRandomTeamsFilters$Request {
  companyId: CompanyId;
  includeUsersWithTeams: boolean;
  goalId?: GoalId;
}

export interface GetAssignRandomTeamsFilters$Response {
  challenges: FilterItemDto[];
  userCount: number;
  availableTeamSlots: number;
  availableTeams: number;
  notEnoughTeams: boolean;
}

export interface GenerateTeams$Request {
  companyId: CompanyId;
  amount: number;
}

export interface GenerateTeams$Response {
  generatedTeamIds: DivisionId[];
}

export interface UpdateTeamSettings$Request {
    companyId: CompanyId;
    divisionRequired: boolean;
    teamSelectionLocked: boolean;
    teamSwitchingDisabled: boolean;
    userCreatedTeams: boolean;
    minTeamSize: number | '';
    maxTeamSize: number | '';
}

export interface UpdateTeamSettings$Response {
}

export interface UpdateDivisionCategorySettings$Request {
    categoryId: DivisionCategoryId;
    type?: DivisionCategoryType; // New category type
    customName?: string; // New custom name (for CUSTOM type)
    required?: boolean; // Whether this category is required
    minSize?: number; // Minimum number of users in this category
    maxSize?: number; // Maximum number of users in this category
    selectionLocked?: boolean; // Whether category selection is locked
    switchingDisabled?: boolean; // Whether switching between categories is disabled
    usersCanCreate?: boolean; // Whether users can create new categories of this type
}

export interface UpdateDivisionCategorySettings$Response {
    // Empty response indicating successful update
}

export interface SetUserCategoryDivision$Request {
    companyId: CompanyId;
    userId: UserId;
    categoryId: DivisionCategoryId;
    divisionId: DivisionId | null; // Can be null to remove assignment
    isAdminView?: boolean; // Indicates if called from admin view (true) or user settings view (false)
}

export interface SetUserCategoryDivision$Response {
    // Empty response indicating successful update
}

export interface GetUserCompanyCountries$Request {
}

export interface GetUserCompanyCountries$Response {
    companyCountries: CompanyCountriesDto[];
}

export interface GetCompanyCountries$Request {
    companyId: CompanyId;
}

export interface GetCompanyCountries$Response {
    companyCountries: CompanyCountriesDto[];
}

export interface GetUserCustomProgress$Request {
    customType: CustomType | null;
    endDate: string;
    goalId: GoalId;
    startDate: string;
    pointsLogicId: string | null;
    userId?: UserId;
}

export interface GetUserDailyDiaryProgress$Request {
    customType: CustomType;
    endDate: string;
    startDate: string;
}

export interface GetUserCustomProgress$Response {
    customDiary: UserCustomDailyProgress[];
}

export interface GetUserDailyDiaryProgress$Response {
    dailyDiary: UserDailyDiaryProgress[];
}

export interface GetTotalPointsProgress$Request {
  goalId: GoalId;
}

export interface GetTotalPointsProgress$Response {
  pointsProgress: TotalPointsProgress[];
}

export interface GetUserDetailedProgress$Request {
    endDate: number;
    startDate: number;
    userId: UserId;
}

export interface GetUserDetailedProgress$Response {
    progressResults: UserDailyProgress[];
}

export interface GetUserGoalProgress$Request {
    goalId: GoalId;
}

export interface GetUserGoalProgress$Response {
    activityProgress: { [key: string ]: ActivityProgress;};
}

export interface GoalDto {
    achievedCount: number;
    activityFormulaTarget: number | null;
    avgProgress: number;
    chatEnabled: boolean;
    collectiveGoal: number | null;
    collectivePercentage: number;
    collectiveTotal: number;
    companyId: CompanyId;
    customGoal: number | null;
    customName: string | null;
    customType: CustomType | null;
    customUnit: string | null;
    dailyPersistenceGoal: number | null;
    dailyStepsGoal: number | null;
    description: string;
    divisionId: DivisionId;
    endDate: number;
    hideCountriesTab: boolean;
    hideGroupsTab: boolean;
    hidePeopleTab: boolean;
    id: GoalId;
    ownerUserId: UserId;
    manualEntryEnabled: boolean;
    participantsCount: number;
    persistenceGoal: number | null;
    progressType: ProgressType;
    startDate: number;
    stepsGoal: number;
    title: string;
    voucher: string;
}

export type GoalId = string;

export interface GoalWithProgress {
    achievedCount: number;
    activityFormulaTarget: number | null;
    avgProgress: number;
    chatEnabled: boolean;
    collectiveGoal: number | null;
    collectivePercentage: number;
    collectiveTotal: number;
    companyId: CompanyId;
    companyLogo: string;
    companyName: string;
    customGoal: number | null;
    customName: string | null;
    customType: CustomType | null;
    customUnit: string | null;
    dailyPersistenceGoal: number | null;
    dailyStepsGoal: number | null;
    description: string;
    divisionId: DivisionId;
    endDate: number;
    hideCountriesTab: boolean;
    hideGroupsTab: boolean;
    hidePeopleTab: boolean;
    id: GoalId;
    manualEntryEnabled: boolean;
    ownerUserId: UserId;
    participantsCount: number;
    participating: boolean;
    persistenceGoal: number | null;
    progressType: ProgressType;
    startDate: number;
    stepsGoal: number;
    title: string;
    totalPercentage: number;
    voucher: string;
    illustrationUrl: string;
    illustrationColor: string;
    logoUrl: string;
    pointsLogic: string;
    roundDailyPoints: boolean;
    autoJoinEnabled: boolean;
    simpleManualEntry: boolean;
    maxManualEntryDays: number;
    oldRoundDailyPoints: boolean;
    countOnlyActiveParticipants: boolean;
    teamsAggregation: GroupAggregationType | null;
    countriesAggregation: GroupAggregationType | null;
    rank: number | null;
    position: number | null;
    teamRank: number | null;
    teamRankFrom: number | null;
    teamTotal: number | null;
    countryRank: number | null;
    countryRankFrom: number | null;
    countryTotal: number | null;
    divisionCategoryRanks: { [categoryId: string]: { rank: number | null; from: number | null; id: DivisionId; total: number | null } };
    persistenceTotal: number;
    stepsTotal: number;
    stepsPercentage: number;
    caloriesTotal: number;
    caloriesGoal: number;
    caloriesPercentage: number;
    intensiveMinutesTotal: number;
    intensiveMinutesGoal: number;
    intensiveMinutesPercentage: number;
    splitRankingView: boolean;
    dailyMax: number;
    leaderboards: string[];
    goals: string; // JSON stringified GoalsConfig
    avgAgg: boolean;
    compactRankingView: boolean;
}

export interface GoogleSignIn$Request {
    authorizationCode: string;
    lang: string;
    nonce: string;
    redirectUri: string;
    signUpCode: string;
}

export interface GoogleSignIn$Response {
    lang: string;
    message: string;
}

export enum GroupView {
    COUNTRIES_MERGED = 'COUNTRIES_MERGED',
    COUNTRIES_SEPARATED = 'COUNTRIES_SEPARATED',
    ONLY_MY_COUNTRY = 'ONLY_MY_COUNTRY',
    ONLY_MY_GROUP = 'ONLY_MY_GROUP',
}

export interface JoinChat$Request {
    goalId: GoalId;
}

export interface JoinChat$Response {
}

export interface JoinGroupChat$Request {
    divisionId: DivisionId;
}

export interface JoinGroupChat$Response {
}

export interface GetGroupMembers$Request {
    divisionId: DivisionId;
}

export interface GroupMemberDto {
    userId: UserId;
    firstName: string;
    lastName: string;
    createdAt: number;
}

export interface GetGroupMembers$Response {
    members: GroupMemberDto[];
}

export interface JoinCompany$Request {
    signUpCode: string;
    userId: UserId;
}

export interface JoinCompany$Response {
    userCompanyId: UserCompanyId;
}

export interface JoinGoal$Request {
    goalId: GoalId;
}

export interface JoinGoal$Response {
}

export enum Lang {
  AR = 'AR',
  BG = 'BG',
  // Not in use
  BS = 'BS',
  CS = 'CS',
  DA = 'DA',
  DE = 'DE',
  EL = 'EL',
  EN = 'EN',
  ES = 'ES',
  ET = 'ET',
  FI = 'FI',
  FR = 'FR',
  HR = 'HR',
  HE = 'HE',
  HU = 'HU',
  ID = 'ID',
  IT = 'IT',
  JA = 'JA',
  KO = 'KO',
  LT = 'LT',
  LV = 'LV',
  NL = 'NL',
  NO = 'NO',
  PL = 'PL',
  PT = 'PT',
  PT_BR = 'PT_BR',
  RO = 'RO',
  RU = 'RU',
  SK = 'SK',
  SL = 'SL',
  SQ = 'SQ',
  SR = 'SR',
  SV = 'SV',
  TH = 'TH',
  TR = 'TR',
  UK = 'UK',
  VI = 'VI',
  ZH_HANS = 'ZH_HANS',
  // Added in 2025 (12 new):
  ZH_HANT = 'ZH_HANT',
  HI = 'HI',
  BN = 'BN',
  PA = 'PA',
  UR = 'UR',
  FA = 'FA',
  MS = 'MS',
  FIL = 'FIL',
  SW = 'SW',
  HA = 'HA',
  MY = 'MY',
  TA = 'TA',
}

export interface LastSyncDatePerActivityDto {
    CALORIES: number;
    HEART_RATE: number;
    HEIGHT: number;
    STEPS: number;
    WEIGHT: number;
}

export interface LeaveCompany$Request {
    companyId: CompanyId;
}

export interface LeaveCompany$Response {
    companyName: string;
}

export interface LeaveGoal$Request {
    goalId: GoalId;
}

export interface LeaveGoal$Response {
}

export interface LogString$Request {
    string: string;
}

export interface LogString$Response {
}

export interface Login$Request {
    email: string;
    password: SensitiveString;
    supportsEmailVerification?: boolean;
    lang?: string;
}

export interface Login$Response {
    lang: Lang;
    sessionId: string;
    requiresEmailVerification: boolean;
}

export interface Logout$Request {
}

export interface Logout$Response {
}

export enum OnboardingStatus {
    ACTIVITY_DATA = 'ACTIVITY_DATA',
    NOTIFICATION_PREFERENCES = 'NOTIFICATION_PREFERENCES',
    ACTIVITY_SYNC_IN_PROGRESS = 'ACTIVITY_SYNC_IN_PROGRESS',
    DONE = 'DONE',
    INTEGRATION = 'INTEGRATION',
    PROFILE_DATA = 'PROFILE_DATA',
    PROFILE_SYNC_IN_PROGRESS = 'PROFILE_SYNC_IN_PROGRESS',
}

export enum PeriodType {
    DAY = 'DAY',
    MONTH = 'MONTH',
    WEEK = 'WEEK',
}

export enum NotificationType {
    ANNOUNCEMENT = 'ANNOUNCEMENT',
    NOTIFICATION = 'NOTIFICATION',
}

export enum UserPreferenceType {
  APP_PUSH_NOTIFICATION = 'APP_PUSH_NOTIFICATION',
  CHAT_PUSH_NOTIFICATION = 'CHAT_PUSH_NOTIFICATION',
  ANNOUNCEMENT_PUSH_NOTIFICATION = 'ANNOUNCEMENT_PUSH_NOTIFICATION',
  NEWSLETTER_EMAIL = 'NEWSLETTER_EMAIL',
  REVIEW_EMAIL = 'REVIEW_EMAIL',
  ANNOUNCEMENT_EMAIL = 'ANNOUNCEMENT_EMAIL',
}

export interface ProfileDto {
    appleId: string;
    companyName: string;
    country: string;
    dateOfBirth: string;
    divisionId: DivisionId;
    divisionName: string;
    firstName: string;
    gender: Gender;
    googleId: string;
    height: number;
    integrationError: string;
    integrationOk: boolean;
    lastName: string;
    lastSynced: number;
    newsletterSubscriber: boolean;
    onboardingStatus: OnboardingStatus;
    passwordSet: boolean;
    serviceProvider: ServiceProvider;
    timeZone: string;
    units: Units;
    weight: number;
}

export enum ProgressType {
    ACTIVITY = 'ACTIVITY',
    ALL = 'ALL',
    CALORIES = 'CALORIES',
    COLLECTIVE_CALORIES = 'COLLECTIVE_CALORIES',
    COLLECTIVE_CUSTOM = 'COLLECTIVE_CUSTOM',
    COLLECTIVE_INTENSIVE_MINUTES = 'COLLECTIVE_INTENSIVE_MINUTES',
    COLLECTIVE_STEPS = 'COLLECTIVE_STEPS',
    COLLECTIVE_POINTS = 'COLLECTIVE_POINTS',
    CUSTOM = 'CUSTOM',
    DISTANCE = 'DISTANCE',
    FORMULA = 'FORMULA',
    INTENSIVE_MINUTES = 'INTENSIVE_MINUTES',
    PERSISTENCE_ALL = 'PERSISTENCE_ALL',
    PERSISTENCE_CALORIES = 'PERSISTENCE_CALORIES',
    PERSISTENCE_CUSTOM = 'PERSISTENCE_CUSTOM',
    PERSISTENCE_CUSTOM_BINARY = 'PERSISTENCE_CUSTOM_BINARY',
    PERSISTENCE_INTENSIVE_MINUTES = 'PERSISTENCE_INTENSIVE_MINUTES',
    PERSISTENCE_STEPS = 'PERSISTENCE_STEPS',
    PERSISTENCE_POINTS = 'PERSISTENCE_POINTS',
    POINTS = 'POINTS',
    TOTAL_POINTS = 'TOTAL_POINTS',
    STEPS = 'STEPS',
    SLEEP_MIN = 'SLEEP_MIN',
}

export enum GroupAggregationType {
  DEFAULT_AVG = 'DEFAULT_AVG',
  DEFAULT_SUM = 'DEFAULT_SUM',
  ONLY_AVG = 'ONLY_AVG',
  ONLY_SUM = 'ONLY_SUM',
}

export interface LeanRedirectView {
    url: string;
}

export interface RegisterUser$Request {
    password: SensitiveString;
}

export interface RegisterUser$Response {
}

export interface ResetPassword$Request {
    newPassword: SensitiveString;
}

export interface ResetPassword$Response {
}

export enum Role {
    ADMIN = 'ADMIN',
    ANONYMOUS = 'ANONYMOUS',
    USER = 'USER',
    AUTHENTICATED = 'AUTHENTICATED',
    SUPER_ADMIN = 'SUPER_ADMIN',
}

export interface SaveGarminOauthRequestToken$Request {
    redirectUrl: string;
}

export interface SaveGarminOauthRequestToken$Response {
    redirectView: LeanRedirectView;
}

export type SensitiveString = string;

export enum ServiceProvider {
    HEALTH_CONNECT= 'HEALTH_CONNECT',
    APPLE_HEALTH = 'APPLE_HEALTH',
    CUSTOM = 'CUSTOM',
    FITBIT = 'FITBIT',
    GARMIN = 'GARMIN',
    GOOGLE_FIT = 'GOOGLE_FIT',
    GOOGLE_FIT_ANDROID = 'GOOGLE_FIT_ANDROID',
    GOOGLE_HEALTH = 'GOOGLE_HEALTH',
    HUAWEI = 'HUAWEI',
    MANUAL = 'MANUAL',
    POLAR = 'POLAR',
    SUUNTO = 'SUUNTO',
    YUMUUV = 'YUMUUV',
}

export type ActivityId = string;

export enum ActivityRowType {
    WALKING = 'WALKING',
    RUNNING = 'RUNNING',
    SWIMMING = 'SWIMMING',
    CYCLING = 'CYCLING',
    YOGA = 'YOGA',
    MINDFULNESS = 'MINDFULNESS',
    OTHER = 'OTHER',
}

export interface ActivityDto {
    /** Unique identifier for the activity */
    id: ActivityId;

    /** When the activity started */
    startTime: number;

    /** When the activity ended */
    endTime: number;

    /** The standardized activity type (e.g., WALKING, RUNNING, etc.) */
    activityType: ActivityRowType;

    /** The standardized activity type */
    originalType: WorkoutType | null;

    /**
     * Normalized fine-grained workout type derived from `originalType`. Used by the client to
     * render the workout icon and label via the catalog in src/common/workoutActivities.ts.
     * `null` when the raw provider string could not be mapped — the client renders the generic
     * fallback in that case.
     */
    normalizedType: WorkoutType | null;

    /** Calories burned during the activity */
    calories: number | null;

    /** Distance covered in meters */
    distance: number | null;

    /** Duration of the activity in seconds */
    duration: number | null;

    /** Number of steps taken during the activity */
    steps: number | null;

    /** The service provider that supplied this activity data */
    serviceProvider: ServiceProvider;

    /** The source that supplied this activity data */
    source: string;

    /** When this activity record was created */
    createdAt: number;

    /** When this activity record was last updated */
    updatedAt: number;

    /** GPS route data for this activity (optional) */
    routes?: Route[];

    /**
     * True when GPS route points exist for this activity. List endpoints leave `routes` empty and
     * set only this flag — fetch the points on demand via getActivityRoute. Always false when the
     * company has location tracking disabled.
     */
    hasRoute?: boolean;

    /**
     * Number of hidden duplicate activities collapsed under this main activity (0 or omitted when
     * there are none). Used to show the "Review duplicates" affordance in the activities list. Not
     * populated for activities returned inside a duplicate group.
     */
    duplicateCount?: number;

    /**
     * `true` when this activity is a multi-sport umbrella (e.g. a triathlon). Its metrics are the
     * sum of `childActivities` and are for display only — the client must NOT add them into any
     * totals (the back-end already excludes it from every stats sum).
     */
    multiSportParent?: boolean;

    /**
     * The individual legs (swim/bike/run/transition) of a multi-sport activity, ordered by start
     * time. Present only on the parent (`multiSportParent`); the legs are nested here instead of
     * appearing as separate top-level list items.
     */
    childActivities?: ActivityDto[];

    /**
     * Optional richer metrics (currently from Garmin), null/omitted when not supplied. Speeds are
     * in metres/second, elevation in metres. The client formats and gates them by activity type.
     * `avgPower` has no Garmin source yet (always null for now).
     */
    avgPower?: number | null;
    avgCadence?: number | null;
    avgHeartRate?: number | null;
    avgSpeed?: number | null;
    maxSpeed?: number | null;
    elevationGain?: number | null;
    elevationLoss?: number | null;

    /**
     * Reverse-geocoded "City, Country" for the activity start point (e.g. "Tallinn, Estonia"). Null
     * or empty when unavailable (no GPS route, not yet geocoded, or no city resolved) — the client
     * then shows just the time.
     */
    locationName?: string | null;
}

export interface GetActivityDuplicates$Request {
    activityId: string;
}

export interface GetActivityRoute$Request {
    /** The activity to fetch the GPS route points for. */
    activityId: string;
}

export interface GetActivityRoute$Response {
    /** Route points ordered by time; empty when none exist or location tracking is disabled. */
    routes: Route[];
}

export interface GetActivityDuplicates$Response {
    /** The full duplicate group (main + duplicates), best-first. */
    activities: ActivityDto[];
    /** Id of the member currently counted as the main. */
    mainActivityId: string;
}

export interface SetMainActivity$Request {
    /** The activity to promote to the counted main of its duplicate group. */
    activityId: string;
}

export interface SetMainActivity$Response {
    /** The refreshed duplicate group after the change. */
    activities: ActivityDto[];
    /** Id of the newly counted main. */
    mainActivityId: string;
}

export interface GetUserActivities$Request {
    // Pagination parameters for infinite scroll
    size?: number; // Number of activities to fetch (replaces limit)
    start?: number; // Start position for pagination (replaces offset)
    // Search parameters for filtering activities
    searchActivityType?: ActivityRowType[]; // Filter by activity types (translated)
    searchOriginalType?: string[]; // Filter by raw provider strings (rarely used directly)
    searchNormalizedType?: WorkoutType[]; // Filter by normalized workout type (expanded server-side)
}

export interface GetUserActivities$Response {
    userActivities: ActivityDto[];
}

export interface SetAppleHealthIntegration$Request {
}

export interface SetAppleHealthIntegration$Response {
}

export interface SetHealthConnectIntegration$Request {
}

export interface SetHealthConnectIntegration$Response {
}

export interface SetNoIntegration$Request {
}

export interface SetNoIntegration$Response {
}

export interface SetOnboardingStatusToActivityData$Request {
}

export interface SetOnboardingStatusToActivityData$Response {
}

export interface SetOnboardingStatusToIntegration$Response {
}

export interface SetUserCountry$Request {
    companyId: CompanyId;
    country: string;
    userId: UserId;
}

export interface SetUserCountry$Response {
}

export interface SetUserDivision$Request {
    companyId: CompanyId;
    divisionId: DivisionId;
    userId: UserId;
}

export interface SetUserDivision$Response {
}

export interface SetUserThirdPartyIntegration$Request {
    authorizationCode: string;
    redirectUrl: string;
    serviceProvider: ServiceProvider;
}

export interface SetUserThirdPartyIntegration$Response {
}

export interface SignUp$Request {
    companyCode: string;
    email: string;
    lang: string;
    password: SensitiveString;
}

export interface SignUp$Response {
    message: string;
    requiresEmailVerification: boolean;
}

export interface VerifyEmailCode$Request {
    code: string;
    purpose: string;
}

export interface VerifyEmailCode$Response {
}

export interface ResendEmailVerificationCode$Request {
    purpose: string;
    lang?: string;
}

export interface ResendEmailVerificationCode$Response {
}

export interface VerifyResetPasswordCode$Request {
    code: string;
}

export interface VerifyResetPasswordCode$Response {
}

export interface Stats {
  type: string;
  result: number;
  startDate: string;
  endDate: string;
  data: string;
  updatedAt: number;
}

export interface StartResetPassword$Request {
    email: string;
    supportsEmailVerification?: boolean;
    lang?: string;
}

export interface StartResetPassword$Response {
}

export interface SyncUserThirdPartyData$Response {
}

export interface ToggleCompanyExcludedFromRanking$Request {
    companyId: CompanyId;
}

export interface ToggleCompanyExcludedFromRanking$Response {
    newExcludedFromRanking: boolean;
}

export interface TogglePublicStatus$Request {
}

export interface TogglePublicStatus$Response {
    newStatus: boolean;
}

export interface ToggleUserStatus$Request {
    companyId: CompanyId;
    userId: UserId;
}

export interface ToggleUserStatus$Response {
    newStatus: boolean;
}

export interface SetUserDevice$Request {
    platform: string;
    osVersion: string;
    appVersion: string;
    token: string;
}

export interface SetUserDevice$Response {

}

export interface RemoveUserDevice$Request {
    token: string;
}

export interface RemoveUserDevice$Response {

}

export enum Units {
    IMPERIAL = 'IMPERIAL',
    METRIC = 'METRIC',
}

export enum ScreenOrientation {
    AUTO = 'AUTO',
    PORTRAIT = 'PORTRAIT',
}

export enum PasswordPolicy {
    DEFAULT = 'DEFAULT',
    POLICY_1 = 'POLICY_1',
    POLICY_2 = 'POLICY_2',
}

export interface UpdateActivityData$Request {
    dateOfBirth: string;
    gender: Gender;
    height: number;
    units: Units;
    weight: number;
    directToPreferences?: boolean;
}

export interface UpdateActivityData$Response {
}

export interface UpdateGoal$Request {
    activityFormulaTarget: number | null;
    chatEnabled: boolean;
    collectiveGoal: number | null;
    companyId: CompanyId;
    customGoal: number | null;
    customName: string | null;
    customType: CustomType | null;
    customUnit: string | null;
    dailyPersistenceGoal: number | null;
    dailyStepsGoal: number | null;
    description: string;
    endDate: number;
    goalId: GoalId;
    hideCountriesTab: boolean;
    hideGroupsTab: boolean;
    hidePeopleTab: boolean;
    manualEntryEnabled: boolean;
    persistenceGoal: number | null;
    progressType: ProgressType;
    startDate: number;
    title: string;
    voucher: string;
    pointsLogic: any[] | null;
    totalPointsLogic: any[] | null;
    roundDailyPoints: boolean;
    autoJoinEnabled: boolean;
    simpleManualEntry: boolean;
    maxManualEntryDays: number;
    includeInTotalPointsChallenges: Record<GoalId, boolean>
    dailyMax: number | null;
    leaderboards: string[];
    goals: GoalsConfig | null;
    avgAgg: boolean;
}

export interface UpdateGoal$Response {
}

export interface GroupAssignment {
    /** The division category ID this assignment belongs to. */
    categoryId: DivisionCategoryId;

    /** The division ID within the category. Can be null if user is not assigned to any division in this category. */
    divisionId: DivisionId;
}

export interface UpdateProfileData$Request {
    country: string;
    divisionId: DivisionId;
    firstName: string;
    lastName: string;
    //newsletterSubscriber: boolean;
    timeZone: string;
    strict: boolean;
    directToPreferences: boolean;
    divisions: GroupAssignment[];
    units?: Units;
}

export interface UpdateProfileData$Response {
}

export interface UpdateUserProfile$Request {
    country: string;
    dateOfBirth: string;
    divisionId: DivisionId;
    firstName: string;
    gender: Gender;
    height: number;
    lastName: string;
    phone?: string;
    // Deprecated
    //newsletterSubscriber: boolean;
    onlyProfileUpdate: boolean;
    ssn?: string;
    timeZone: string;
    units: Units;
    weight: number;
}

export interface UpdateUserProfile$Response {
}

export interface UploadAllAppleHealthActivities$Request {
    appleHealthActivities: UploadActivityDto[];
    activityEntries: ActivityEntry[];
    deletedActivities: any[];
    timeZone: string;
}

export interface UploadAllAppleHealthActivities$Response {
}

export interface UploadAllGoogleFitActivities$Request {
    googleFitActivities: UploadActivityDto[];
    activityEntries: ActivityEntry[];
    timeZone: string;
}

export interface UploadAllGoogleFitActivities$Response {
}

export interface UploadAllHealthConnectActivities$Request {
    healthConnectActivities: UploadActivityDto[];
    activityEntries: ActivityEntry[];
    deletedActivities: any[];
    timeZone: string;
}

export interface UploadAllHealthConnectActivities$Response {
}

export interface UploadActivity$Request {
    /** The YuMuuv activity to upload and store */
    activity: ActivityEntry;
    /** Optional GPS route data for the activity */
    route?: any[];
}

export interface UploadActivity$Response {
    success: boolean;
    activityId?: string;
}

export interface UserCompanyDto {
    active: boolean;
    companyId: CompanyId;
    companyName: string;
    country: string;
    countryRequired: boolean;
    createdAt: number;
    divisionId: DivisionId;
    divisionName: string;
    divisionRequired: boolean;
    employees: number;
    hideRankingView: boolean;
    hideStatisticsView: boolean;
    reportRequired: boolean;
    reportRequiredForUsers: boolean;
    id: UserCompanyId;
    ownerCompanyId: CompanyId;
    ownerUserId: UserId;
    publicGoalsEnabled: boolean;
    scope: string;
    userGoalsEnabled: boolean;
    userId: UserId;
    userLimit: number;
    brandLogoUrl: string;
    plan: string;
    trialStartDate: string;
    trialEndDate: string;
    startAt: string;
    paidUntil: string;
    salesperson: string;
    salespersonEmail: string;
    passwordPolicy: string;
    passwordMinLength: number;
    minTeamSize: number;
    maxTeamSize: number;
    teamSelectionLocked: boolean;
    userCreatedTeams: boolean;
    autoJoinEnabled: boolean;
    teamSwitchingDisabled: boolean;
    customPolicyUrl: string;
    customPolicyTitle: string;
    divisionIds: string; // JSON string with structure: {"categoryId": "divisionId"}
}

export type UserCompanyId = string;

export interface UserCompanyRolesDto {
    companyId: CompanyId;
    roles: Role[];
    userId: UserId;
}

export interface UserCustomDailyProgress {
    date: string;
    value: number;
    // PointsDiaryData[]
    data: any;
}

export interface TotalPointsProgress {
    id: GoalId;
    title: string;
    startDate: string;
    endDate: string;
    points: number;
}

export interface UserDailyDiaryProgress {
    date: string;
    integrationValue: number;
    manualValue: number;
    sum: number;
}

export interface UserDailyProgress {
    caloriesGoal: number;
    caloriesPercentage: number;
    caloriesTotal: number;
    date: string;
    distanceKm: number;
    intensiveMinutesGoal: number;
    intensiveMinutesPercentage: number;
    intensiveMinutesTotal: number;
    stepsGoal: number;
    stepsPercentage: number;
    stepsTotal: number;
    totalPercentage: number;
    integrationTotalPercentage: number;
}

export interface UserDto {
    active: boolean;
    appleId: string;
    companyId: CompanyId;
    companyName: string;
    country: string;
    createdAt: number;
    dateOfBirth: string;
    divisionId: DivisionId;
    divisionName: string;
    email: string;
    firstName: string;
    gender: Gender;
    googleId: string;
    height: number;
    integrationError: string;
    lang: Lang;
    lastName: string;
    lastSync: number;
    newsletterSubscriber: boolean;
    onboardingStatus: OnboardingStatus;
    role: Role;
    serviceProvider: string;
    timeZone: string;
    units: Units;
    userId: UserId;
    weight: number;
    // Division category assignments as JSON string for the new group system
    divisionIds?: string;
}

export interface UserProfileDto {
    active: boolean;
    appleId: string;
    country: string;
    createdAt: number;
    dateOfBirth: string;
    email: string;
    firstName: string;
    gender: Gender;
    googleId: string;
    height: number;
    integrationError: string;
    lang: Lang;
    lastName: string;
    lastSync: number;
    newsletterSubscriber: boolean;
    onboardingStatus: OnboardingStatus;
    //role: Role;
    serviceProvider: ServiceProvider;
    timeZone: string;
    units: Units;
    screenOrientation: ScreenOrientation;
    userId: UserId;
    weight: number;
    passwordSet: boolean;
    unreadNotificationsCount?: number;
    allowManualEntry: boolean;
    emailVerified?: boolean;
    phone?: string;
    ssn?: string;
}

export interface UserGoal {
    activityProgress: ActivityProgress;
    goalDto: GoalDto;
}

export type UserGoalResultId = string;

export type UserId = string;

export interface WhoAmI$Response {
    authenticated: boolean;
    resetPasswordInProgress: boolean;
    userCompanies: UserCompanyDto[];
    userCompanyRoles: UserCompanyRolesDto[];
    userDto: UserProfileDto;
}

export interface GetWhoAmI$Request {
    platform: string;
    osVersion: string;
    appVersion: string;
}

export interface GetWhoAmI$Response {
    authenticated: boolean;
    resetPasswordInProgress: boolean;
    emailVerificationInProgress: boolean;
    userCompanies: UserCompanyDto[];
    userCompanyRoles: UserCompanyRolesDto[];
    userDto: UserProfileDto;
    session: string
    pushNotifications: boolean
}

export interface SearchCompanies$Request {
    query: string;
    page: number;
    pageSize: number;
}

export interface SearchCompanies$Response {
    companies: SearchCompaniesListItem[];
    totalCount: number;
}

export interface SearchCompaniesListItem {
    companyId: CompanyId;
    companyName: string;
    employees: number;
    plan: string;
    scope: string;
    startAt: string;
    paidUntil: string;
}

export interface GetCompanyDetails$Request {
    signUpCode: string;
}

export interface GetCompanyDetails$Response {
    passwordPolicy: string;
    passwordMinLength: number;
    customPolicyUrl: string;
    customPolicyTitle: string;
    // SSO fields -- returned when the company has an active SSO config with JIT provisioning
    ssoEnabled: boolean;
    ssoRequired: boolean;
    companyId: string;
}

export interface BulkDeleteUsers$Request {
    companyId: CompanyId;
    userIds: UserId[];
}

export interface BulkDeleteUsers$Response {
}

export interface BulkAddAdminRole$Request {
    companyId: CompanyId;
    userIds: UserId[];
}

export interface BulkAddAdminRole$Response {
}

export interface BulkRemoveAdminRole$Request {
    companyId: CompanyId;
    userIds: UserId[];
}

export interface BulkRemoveAdminRole$Response {
}

export interface DeleteActivity$Request {
    activityId: string;
}

export interface DeleteActivity$Response {
    success: boolean;
}


// --- SSO (Single Sign-On) types ---

// Login method enum returned by checkLoginMethod RPC.
// Determines what the user should do next after entering their email.
export enum LoginMethod {
    PASSWORD = 'PASSWORD',
    SSO_REQUIRED = 'SSO_REQUIRED',
    SSO_OR_PASSWORD = 'SSO_OR_PASSWORD',
    ACCOUNT_NOT_FOUND = 'ACCOUNT_NOT_FOUND',
}

// SSO protocol type (SAML for now, OIDC planned for future)
export enum SsoProtocol {
    SAML = 'SAML',
    OIDC = 'OIDC',
}

// CheckLoginMethod: email-first lookup to determine login flow
export interface CheckLoginMethod$Request {
    email: string;
}

export interface CheckLoginMethod$Response {
    loginMethod: LoginMethod;
    companyId?: string;
    companyName?: string;
    ssoConfigId?: string;
}

// --- SCIM types ---

export type DeprovisionAction = 'DEACTIVATE' | 'DELETE'
export type ScimGroupMapping = 'NONE' | 'DIVISIONS' | 'TEAMS'

export interface ScimConfigDto {
    id: string;
    ssoConfigId: string;
    syncEnabled: boolean;
    deprovisionAction: DeprovisionAction;
    groupMapping: ScimGroupMapping;
    divisionCategoryId?: string;
    lastSyncAt?: string;
    endpointUrl: string;
}

// --- SSO Config: List all configs for a company ---

export interface GetCompanySsoConfigs$Request {
    companyId: string;
}
export interface GetCompanySsoConfigs$Response {
    configs: SsoConfigListItem[];
}
export interface SsoConfigListItem {
    id: string;
    name: string;
    protocol: SsoProtocol;
    active: boolean;
    domains: string[];
}

// --- SSO Config: Get single config detail ---

export interface GetCompanySsoConfig$Request {
    ssoConfigId: string;
}

export interface GetCompanySsoConfig$Response {
    configured: boolean;
    configId?: string;
    companyId?: string;
    name?: string;
    protocol?: SsoProtocol;
    ssoRequired?: boolean;
    jitProvisioningEnabled?: boolean;
    idpEntityId?: string;
    idpSsoUrl?: string;
    idpCertificate?: string;

    spEntityId?: string;
    spMetadataUrl?: string;
    active?: boolean;
    linkedDomainIds?: string[];
    scimConfig?: ScimConfigDto;
    createdAt?: string;
    updatedAt?: string;
}

// --- SSO Config: Create new config ---

export interface CreateCompanySsoConfig$Request {
    companyId: string;
    name: string;
    protocol: SsoProtocol;
    ssoRequired: boolean;
    jitProvisioningEnabled: boolean;
    idpEntityId?: string;
    idpSsoUrl?: string;
    idpCertificate?: string;

    active: boolean;
    linkedDomainIds: string[];
}
export interface CreateCompanySsoConfig$Response {
    status: string;
    configId: string;
}

// --- SSO Config: Update existing config ---

export interface UpdateCompanySsoConfig$Request {
    ssoConfigId: string;
    name?: string;
    protocol?: SsoProtocol;
    ssoRequired?: boolean;
    jitProvisioningEnabled?: boolean;
    idpEntityId?: string;
    idpSsoUrl?: string;
    idpCertificate?: string;

    active?: boolean;
    linkedDomainIds?: string[];
}
export interface UpdateCompanySsoConfig$Response {
    status: string;
    configId: string;
}

// --- SSO Config: Delete config ---

export interface DeleteCompanySsoConfig$Request {
    ssoConfigId: string;
}

export interface DeleteCompanySsoConfig$Response {
}

// --- SSO Config: Backward-compatible aliases ---

// @deprecated Use CreateCompanySsoConfig$Request / UpdateCompanySsoConfig$Request instead
export interface ConfigureCompanySso$Request {
    companyId: string;
    protocol: SsoProtocol;
    ssoRequired: boolean;
    jitProvisioningEnabled: boolean;
    idpEntityId?: string;
    idpSsoUrl?: string;
    idpCertificate?: string;

    active: boolean;
}

// @deprecated Use CreateCompanySsoConfig$Response / UpdateCompanySsoConfig$Response instead
export interface ConfigureCompanySso$Response {
}

// --- SCIM: Enable/Disable/Regenerate ---

export interface EnableScim$Request {
    ssoConfigId: string;
    deprovisionAction: DeprovisionAction;
    groupMapping: ScimGroupMapping;
    divisionCategoryId?: string;
}
export interface EnableScim$Response {
    scimConfig: ScimConfigDto;
    bearerToken: string;
}

export interface DisableScim$Request {
    ssoConfigId: string;
}
export interface DisableScim$Response {
}

export interface RegenerateScimToken$Request {
    ssoConfigId: string;
}
export interface RegenerateScimToken$Response {
    bearerToken: string;
}

// --- Domain Verification ---

// Domain verification status
export type DomainStatus = 'pending' | 'verified' | 'failed'
export type DomainVerificationMethod = 'manual' | 'dns_txt'

// Represents a company's verified (or pending) email domain
export interface CompanyDomain {
    id: string;
    companyId: string;
    ssoConfigId?: string;
    domain: string;
    status: DomainStatus;
    verificationMethod: DomainVerificationMethod;
    verificationToken: string;
    verifiedAt: string | null;
    createdAt: string;
    updatedAt: string;
}

// GetCompanyDomains: list all domains for a company
export interface GetCompanyDomains$Request {
    companyId: string;
}
export interface GetCompanyDomains$Response {
    domains: CompanyDomain[];
}

// AddCompanyDomain: add a new domain to a company
export interface AddCompanyDomain$Request {
    companyId: string;
    domain: string;
}
export interface AddCompanyDomain$Response {
    domain: CompanyDomain;
}

// RemoveCompanyDomain: remove a domain from a company
export interface RemoveCompanyDomain$Request {
    companyId: string;
    domainId: string;
}
export interface RemoveCompanyDomain$Response {
}

// VerifyCompanyDomain: trigger verification for a domain
export interface VerifyCompanyDomain$Request {
    companyId: string;
    domainId: string;
}
export interface VerifyCompanyDomain$Response {
    domain: CompanyDomain;
}

// --- Sign-Up Codes ---

export interface SignUpCodeItem {
    id: string;
    code: string;
    role: Role;
    expiresAt: string | null;
    divisionId: DivisionId;
    usageLimit: number | null;
    usageCount: number;
    createdAt: string;
    updatedAt: string;
}

export interface GetCompanySignUpCodes$Request {
    companyId: string;
}
export interface GetCompanySignUpCodes$Response {
    codes: SignUpCodeItem[];
}

export interface CreateCompanySignUpCode$Request {
    companyId: string;
    code?: string | null;
    role: Role;
    expiresAt?: string | null;
    divisionId?: string | null;
    usageLimit?: number | null;
}
export interface CreateCompanySignUpCode$Response {
    id: string;
    code: string;
}

export interface UpdateCompanySignUpCode$Request {
    companyId: string;
    signUpCodeId: string;
    code?: string | null;
    role?: Role;
    expiresAt?: string | null;
    divisionId?: string | null;
    usageLimit?: number | null;
}
export interface UpdateCompanySignUpCode$Response {
    status: string;
}

export interface DeleteCompanySignUpCode$Request {
    companyId: string;
    signUpCodeId: string;
}
export interface DeleteCompanySignUpCode$Response {
    status: string;
}

// --- Company Info ---

export interface GetCompanyInfo$Request {
    companyId: string;
}
export interface GetCompanyInfo$Response {
    name: string;
    brandLogoUrl: string;
    countries: string;
    hideStatisticsView: boolean;
    hideRankingView: boolean;
    countryRequired: boolean;
    userGoalsEnabled: boolean;
    autoJoinEnabled: boolean;
    reportRequired: boolean;
    reportRequiredForUsers: boolean;
    passwordMinLength: number;
    customPolicyUrl: string;
    emailVerificationRequired: boolean;
    divisionRequired: boolean;
    teamSelectionLocked: boolean;
    teamSwitchingDisabled: boolean;
    userCreatedTeams: boolean;
    minTeamSize: number;
    maxTeamSize: number;
}

export interface UpdateCompanyInfo$Request {
    companyId: string;
    name: string;
    brandLogoUrl: string;
    countries: string;
    hideStatisticsView: boolean;
    hideRankingView: boolean;
    countryRequired: boolean;
    userGoalsEnabled: boolean;
    autoJoinEnabled: boolean;
    reportRequired: boolean;
    reportRequiredForUsers: boolean;
    passwordMinLength: number;
    customPolicyUrl: string;
    emailVerificationRequired: boolean;
}
export interface UpdateCompanyInfo$Response {
}

// --- Feature Flags ---
export interface GetCompanyFeatureFlags$Request {
    companyId: string;
}
export interface GetCompanyFeatureFlags$Response {
    features: { key: string; enabled: boolean; defaultEnabled: boolean }[];
}
export interface UpdateCompanyFeatureFlags$Request {
    companyId: string;
    features: Record<string, boolean>;
    secret: string;
}
export interface UpdateCompanyFeatureFlags$Response {
}
